import {
  type AiBuilderMessageAttachment,
  type AiBuilderSelectedFile,
} from '../stores/types';
import { type PdfPageRange } from '../utils/pdfPageRanges';

const URL_PATTERN = /https?:\/\/[^\s]+/i;

export const createUserAttachment = (
  prompt: string,
  selectedFile: AiBuilderSelectedFile | null,
): AiBuilderMessageAttachment | undefined => {
  if (selectedFile) {
    return {
      type: 'file',
      file: selectedFile.file,
      label: selectedFile.file.name,
      extension: getAiBuilderFileExtension(selectedFile.file.name),
      ...(selectedFile.pageRanges
        ? {
            pdfSelection: {
              pageCount: selectedFile.pageCount,
              pageRanges: cloneCanonicalPageRanges(
                selectedFile.pageRanges,
                selectedFile.pageCount,
              ),
            },
          }
        : {}),
    };
  }

  const url = prompt.match(URL_PATTERN)?.[0];

  if (!url) return undefined;

  return {
    type: 'url',
    label: url,
  };
};

export const getAiBuilderFileExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()?.trim().toLowerCase();

  return extension && extension !== fileName.toLowerCase()
    ? extension
    : undefined;
};

const cloneCanonicalPageRanges = (
  pageRanges: readonly PdfPageRange[],
  pageCount: number,
) => {
  const normalizedRanges = pageRanges
    .filter(
      ({ end, start }) =>
        Number.isInteger(start) &&
        Number.isInteger(end) &&
        end >= 1 &&
        start <= pageCount,
    )
    .map(({ end, start }) => ({
      start: Math.max(1, start),
      end: Math.min(pageCount, end),
    }))
    .filter(({ end, start }) => start <= end)
    .sort((left, right) => left.start - right.start || left.end - right.end);

  return normalizedRanges.reduce<PdfPageRange[]>((canonical, range) => {
    const previous = canonical.at(-1);
    if (previous && range.start <= previous.end + 1) {
      previous.end = Math.max(previous.end, range.end);
    } else {
      canonical.push({ ...range });
    }
    return canonical;
  }, []);
};
